import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  Chip
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";

// Импорт компонентов
import AssetBasicInfo, { AssetFinancialInfo, AssetDates } from "./components/AssetBasicInfo";
import AssetTechnicalSpecs from "./components/AssetTechnicalSpecs";
import AssetSearchMetadata from "./components/AssetSearchMetadata";
import AssetStatistics from "./components/AssetStatistics";
import AssetMovementHistory from "./components/AssetMovementHistory";
import AssetDialogs from "./components/AssetDialogs";
import AssetPhotos from "./components/AssetPhotos";
import AssetAIEnrichment from "./components/AssetAIEnrichment";

// Импорт утилит
import { validateAsset, prepareAssetForSave } from "./utils/assetHelpers";
import { DEFAULT_ASSET_STATE } from "./utils/assetConstants";

import api from "../../../api/api";

const AssetsEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [activeSection, setActiveSection] = useState("asset-basic");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [referenceError, setReferenceError] = useState('');
  const savingRef = useRef(false);
  const dialogSavingRef = useRef(false);
  
  const isNewAsset = id === "new";
  const [asset, setAsset] = useState(DEFAULT_ASSET_STATE);

  // Обработка данных копирования при создании нового актива
  useEffect(() => {
    if (isNewAsset && location.state?.copyData) {
      console.log('📋 Копирование актива. Данные:', location.state.copyData);
      setAsset(prev => {
        const copyData = location.state.copyData;
        
        const copiedAsset = {
          ...DEFAULT_ASSET_STATE,
          ...copyData,
          
          // Убираем поля, которые не должны копироваться
          id: undefined,
          created_at: undefined,
          updated_at: undefined,
          company_id: undefined,
          founder_id: undefined,
          manager_id: undefined,
          asset_number: undefined,
          inventory_number: '',
          current_deal_id: null,
          locker_cell_id: null,
          courier_handover: false,
          
          // Маппим backend поля в frontend поля (если они есть)
          technical_specifications: copyData.specifications || copyData.technical_specifications || {},
          
          // Маппим search_metadata правильно
          search_metadata: {
            ...copyData.search_metadata,
            tags: copyData.ai_tags || copyData.search_metadata?.tags || [],
            target_audience: copyData.search_metadata?.target_audience || [],
            use_cases: copyData.search_metadata?.use_cases || []
          },
          
          // Маппим связанные объекты в ID и названия
          asset_status_id: null,
          status: '',
          rental_point_id: copyData.rental_point?.id || copyData.rental_point_id,
          rental_point: copyData.rental_point?.name || copyData.rental_point,
          
          // Обнуляем тариф при копировании (как в handleCopy)
          tariff_id: null,
          tariff: "",
        };
        
        // Удаляем backend поля, чтобы не было дублирования
        delete copiedAsset.specifications;
        delete copiedAsset.ai_tags;
        delete copiedAsset.asset_status;
        delete copiedAsset.selected_tariff;
        
        console.log('✅ Установлены данные копируемого актива:', copiedAsset);
        return copiedAsset;
      });
    }
  }, [isNewAsset, location.state]);

  // Универсальный обработчик изменений
  const handleChange = (field, value) => {
    console.log('🔄 Изменение поля:', field, 'Новое значение:', value);
    console.log('📊 Состояние asset ДО изменения:', asset);
    
    // Если value - это функция (для сложных обновлений), вызываем её
    if (typeof value === 'function') {
      setAsset(value);
    } else {
      // Обычное обновление поля
      setAsset(prev => {
        const updated = {
          ...prev,
          [field]: value
        };
        console.log('📊 Состояние asset ПОСЛЕ изменения:', updated);
        return updated;
      });
    }
  };

  // Состояния для справочников
  const [statuses, setStatuses] = useState([]);
  const [rentalPoints, setRentalPoints] = useState([]);
  const [tariffs, setTariffs] = useState([]);

  // Состояния для диалогов
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");
  
  const [rentalPointDialogOpen, setRentalPointDialogOpen] = useState(false);
  const [newRentalPointName, setNewRentalPointName] = useState("");
  const [newRentalPointAddress, setNewRentalPointAddress] = useState("");
  
  const [tariffDialogOpen, setTariffDialogOpen] = useState(false);
  const [newTariff, setNewTariff] = useState({
    name: "",
    price: "",
    days: 0,
    hours: 0,
    minutes: 0
  });

  // Загрузка справочников
  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await Promise.allSettled([
          api.get("/asset-statuses/"),
          api.get("/rental-points/"),
          api.get("/rental-tariffs/")
        ]);

        [setStatuses, setRentalPoints, setTariffs].forEach((setter, index) => {
          if (results[index].status === 'fulfilled') setter(results[index].value.data || []);
        });
        if (results.some(result => result.status === 'rejected')) {
          setReferenceError('Не удалось загрузить часть справочников. Обновите страницу перед выбором статуса, пункта или тарифа.');
        }
      } catch (error) {
        console.error("Ошибка загрузки справочников:", error);
        setReferenceError('Ошибка загрузки справочников');
      }
    };

    fetchData();
  }, []);

  // Загрузка данных актива при редактировании
  useEffect(() => {
    let active = true;
    const fetchAsset = async () => {
      if (isNewAsset) return;

      try {
        setLoading(true);
        setLoadError('');
        console.log('📥 Загружаем актив с ID:', id);
        const response = await api.get(`/assets/rental/${id}`);
        if (!active) return;
        if (!response.data) throw new Error('Empty asset response');
        
        console.log('📥 Полный ответ от сервера:', response.data);
        console.log('🔧 specifications от сервера:', response.data.specifications);
        console.log('🏷️ ai_tags от сервера:', response.data.ai_tags);
        console.log('🔍 search_metadata от сервера:', response.data.search_metadata);
        
        if (response.data) {
          // Маппинг backend -> frontend
          const loadedAsset = {
            ...DEFAULT_ASSET_STATE,
            ...response.data,
            // ИСПРАВЛЕНИЕ: Правильный маппинг specifications -> technical_specifications
            technical_specifications: response.data.specifications || {},
            // Маппинг search_metadata
            search_metadata: {
              ...response.data.search_metadata,
              tags: response.data.ai_tags || [],
              target_audience: response.data.search_metadata?.target_audience || [],
              use_cases: response.data.search_metadata?.use_cases || []
            },
            // КРИТИЧНО: Маппинг связанных объектов в ID и названия
            asset_status_id: response.data.asset_status?.id || response.data.asset_status_id,
            status: response.data.asset_status?.name || response.data.status,
            tariff_id: response.data.selected_tariff?.id || response.data.tariff_id,
            tariff: response.data.selected_tariff?.name || response.data.tariff,
            rental_point_id: response.data.rental_point?.id || response.data.rental_point_id,
            rental_point: response.data.rental_point?.name || response.data.rental_point
          };
          
          // Удаляем backend поля, чтобы не было дублирования
          delete loadedAsset.specifications;
          delete loadedAsset.ai_tags;
          delete loadedAsset.asset_status;
          delete loadedAsset.selected_tariff;
          
          setAsset(loadedAsset);
          console.log('✅ Asset установлен в состояние:', loadedAsset);
          console.log('🔧 technical_specifications в состоянии:', loadedAsset.technical_specifications);
          console.log('🏷️ search_metadata.tags в состоянии:', loadedAsset.search_metadata?.tags);
        }
      } catch (error) {
        if (!active) return;
        console.error("Ошибка загрузки актива:", error);
        setLoadError('Ошибка загрузки актива');
        enqueueSnackbar("Ошибка загрузки актива", { variant: "error" });
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchAsset();
    return () => { active = false; };
  }, [id, isNewAsset, enqueueSnackbar]);

  // Установка названия тарифа после загрузки тарифов
  useEffect(() => {
    if (asset.tariff_id && tariffs.length > 0) {
      const selectedTariff = tariffs.find(t => t.id === parseInt(asset.tariff_id));
      if (selectedTariff && asset.tariff !== selectedTariff.name) {
        setAsset(prev => ({ ...prev, tariff: selectedTariff.name }));
      }
    }
      }, [asset.tariff_id, tariffs, asset.tariff]);

  // Установка дефолтного статуса для нового актива
  useEffect(() => {
    if (isNewAsset && statuses.length > 0 && !asset.asset_status_id) {
      const defaultStatus = statuses.find(s => s.status_key === "available") || statuses[0];
      if (defaultStatus) {
        console.log('🆕 Устанавливаем дефолтный статус для нового актива:', defaultStatus);
        setAsset(prev => ({
          ...prev,
          asset_status_id: defaultStatus.id,
          status: defaultStatus.name
        }));
      }
    }
  }, [isNewAsset, statuses, asset.asset_status_id]);

  // Обработчик сохранения
  const handleSave = async () => {
    if (savingRef.current || loadError) return;
    console.log('💾 Начинаем сохранение актива');
    console.log('📊 Состояние asset перед валидацией:', asset);
    
    const errors = validateAsset(asset);
    if (errors.length > 0) {
      errors.forEach(error => {
        enqueueSnackbar(error, { variant: "error" });
      });
      return;
    }

    savingRef.current = true;
    try {
      setLoading(true);
      console.log('💾 Asset перед подготовкой:', asset);
      console.log('📊 technical_specifications перед сохранением:', asset.technical_specifications);
      console.log('🏷️ search_metadata перед сохранением:', asset.search_metadata);
      
      // Подготавливаем данные для отправки
      const dataToSave = prepareAssetForSave(asset);
      
      console.log('📤 Данные для отправки на сервер:', dataToSave);
      console.log('📊 specifications для сервера:', dataToSave.specifications);
      console.log('📊 technical_specifications для сервера:', dataToSave.technical_specifications);
      console.log('🏷️ ai_tags для сервера:', dataToSave.ai_tags);

      let response;
      if (isNewAsset) {
        response = await api.post("/assets/rental", dataToSave);
        console.log('✅ Актив создан:', response.data);
      } else {
        response = await api.put(`/assets/rental/${id}`, dataToSave);
        console.log('✅ Актив обновлен:', response.data);
      }

      if (response.data) {
        enqueueSnackbar(
          isNewAsset ? "Актив успешно создан" : "Актив успешно обновлен", 
          { variant: "success" }
        );
        
        // Возвращаемся назад (туда откуда пришли)
        navigate(-1);
      }
    } catch (error) {
      console.error("❌ Ошибка сохранения:", error);
      console.error("🔍 Ответ сервера:", error.response?.data);
      enqueueSnackbar(`Ошибка: ${error.response?.data?.detail || error.message}`, { variant: "error" });
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  // Создание статуса
  const createStatus = async () => {
    if (!newStatusName.trim() || dialogSavingRef.current) return;
    dialogSavingRef.current = true;

    try {
      const response = await api.post("/asset-statuses/", { name: newStatusName });
      if (response.data) {
        setStatuses(prev => [...prev, response.data]);
        setAsset(prev => ({ 
          ...prev, 
          asset_status_id: response.data.id,
          status: response.data.name
        }));
        setStatusDialogOpen(false);
        setNewStatusName("");
        enqueueSnackbar("Статус успешно создан", { variant: "success" });
      }
    } catch (error) {
      console.error("Ошибка создания статуса:", error);
      enqueueSnackbar("Ошибка создания статуса", { variant: "error" });
    } finally {
      dialogSavingRef.current = false;
    }
  };

  // Создание пункта проката
  const createRentalPoint = async () => {
    if (!newRentalPointName.trim() || dialogSavingRef.current) return;
    dialogSavingRef.current = true;

    try {
      const response = await api.post("/rental-points/", {
        name: newRentalPointName,
        address: newRentalPointAddress
      });
      
      if (response.data) {
        setRentalPoints(prev => [...prev, response.data]);
        setAsset(prev => ({ 
          ...prev, 
          rental_point_id: response.data.id,
          rental_point: response.data.name
        }));
        setRentalPointDialogOpen(false);
        setNewRentalPointName("");
        setNewRentalPointAddress("");
        enqueueSnackbar("Пункт проката успешно создан", { variant: "success" });
      }
    } catch (error) {
      console.error("Ошибка создания пункта проката:", error);
      enqueueSnackbar("Ошибка создания пункта проката", { variant: "error" });
    } finally {
      dialogSavingRef.current = false;
    }
  };

  // Создание тарифа
  const createTariff = async () => {
    if (!newTariff.name.trim() || String(newTariff.price).trim() === '' || dialogSavingRef.current) return;
    if (!Number.isFinite(Number(newTariff.price)) || Number(newTariff.price) < 0 ||
        ['days', 'hours', 'minutes'].some(key => !Number.isInteger(Number(newTariff[key])) || Number(newTariff[key]) < 0)) {
      enqueueSnackbar('Цена должна быть неотрицательной, а срок — целым неотрицательным числом', { variant: 'error' });
      return;
    }
    dialogSavingRef.current = true;

    try {
      const response = await api.post("/rental-tariffs/", newTariff);
      if (response.data) {
        setTariffs(prev => [...prev, response.data]);
        setAsset(prev => ({ 
          ...prev, 
          tariff_id: response.data.id,
          tariff: response.data.name 
        }));
        setTariffDialogOpen(false);
        setNewTariff({ name: "", price: "", days: 0, hours: 0, minutes: 0 });
        enqueueSnackbar("Тариф успешно создан", { variant: "success" });
      }
    } catch (error) {
      console.error("Ошибка создания тарифа:", error);
      enqueueSnackbar("Ошибка создания тарифа", { variant: "error" });
    } finally {
      dialogSavingRef.current = false;
    }
  };

  if (loading && isNewAsset === false) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) return <Box sx={{ p: 3 }}><Button onClick={() => navigate(-1)}>Назад</Button><Alert severity="error">{loadError}</Alert></Box>;

  return (
    <Box className="erp-editor erp-asset-editor" sx={{ maxWidth: 1480, mx: "auto", p: 3 }}>
      <Box className="asset-editor-heading">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} variant="outlined">Назад</Button>
        <Box className="asset-editor-title">
          <Typography variant="h4" component="h1">{isNewAsset ? "Создание нового актива" : "Редактирование актива"}</Typography>
          {!isNewAsset && <Typography color="text.secondary" variant="body2">{asset.name}</Typography>}
        </Box>
        {asset.status && <Chip label={asset.status} variant="outlined" color="primary" />}
        <AssetAIEnrichment asset={asset} disabled={loading} onApply={changes => {
          setAsset(previous => ({ ...previous, ...changes }));
          enqueueSnackbar('Данные добавлены в форму. Проверьте и сохраните актив.', { variant: 'success' });
        }} />
        <Button className="asset-top-save" variant="contained" onClick={handleSave} disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}>
          {loading ? 'Сохраняется...' : 'Сохранить изменения'}
        </Button>
      </Box>
      <Paper component="nav" aria-label="Разделы актива" className="asset-section-nav" elevation={0}>
        {[
          ['asset-basic', 'Основная информация'], ['asset-finance', 'Финансовые параметры'],
          ['asset-photos', 'Фотографии'], ['asset-dates', 'Даты'],
          ...(!isNewAsset ? [['asset-history', 'История']] : []), ['asset-extra', 'Дополнительно']
        ].map(([target, label]) => <a key={target} aria-current={activeSection === target ? "location" : undefined} href={`#${target}`} onClick={event => {
          event.preventDefault();
          setActiveSection(target);
          const section = document.getElementById(target);
          section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const focusTarget = section?.querySelector('input, button, textarea');
          focusTarget?.focus({ preventScroll: true });
        }}>{label}</a>)}
      </Paper>

      {/* Основная информация */}
      {referenceError && <Alert severity="error" sx={{ mb: 2 }}>{referenceError}</Alert>}
      <div className="erp-asset-grid"><div>
      <AssetBasicInfo
        separateSections
        asset={asset}
        onChange={handleChange}
        statuses={statuses}
        rentalPoints={rentalPoints}
        tariffs={tariffs}
        onCreateStatus={() => setStatusDialogOpen(true)}
        onCreateRentalPoint={() => setRentalPointDialogOpen(true)}
        onCreateTariff={() => setTariffDialogOpen(true)}
      />

      <AssetFinancialInfo asset={asset} onChange={handleChange} />
      </div><div>
      <AssetPhotos
        images={asset.images}
        onChange={(images) => handleChange("images", images)}
      />

      <AssetDates asset={asset} onChange={handleChange} />
      </div></div>
      <Box id="asset-extra" className="asset-extra-grid">
        <AssetTechnicalSpecs asset={asset} onChange={handleChange} />
        <AssetSearchMetadata asset={asset} handleChange={handleChange} />
      </Box>
      {!isNewAsset && <Box id="asset-history">
        <AssetMovementHistory assetId={id} rentalPoints={rentalPoints} />
        <AssetStatistics assetId={id} />
      </Box>}
      {/* Кнопки управления */}
      <Paper className="erp-savebar" elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Отмена
          </Button>
          
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveOutlinedIcon />}
          >
            {loading ? 'Сохраняется...' : 'Сохранить актив'}
          </Button>
        </Box>
      </Paper>

      {/* Модальные окна */}
      <AssetDialogs
        statusDialogOpen={statusDialogOpen}
        setStatusDialogOpen={setStatusDialogOpen}
        newStatusName={newStatusName}
        setNewStatusName={setNewStatusName}
        onCreateStatus={createStatus}
        
        rentalPointDialogOpen={rentalPointDialogOpen}
        setRentalPointDialogOpen={setRentalPointDialogOpen}
        newRentalPointName={newRentalPointName}
        setNewRentalPointName={setNewRentalPointName}
        newRentalPointAddress={newRentalPointAddress}
        setNewRentalPointAddress={setNewRentalPointAddress}
        onCreateRentalPoint={createRentalPoint}
        
        tariffDialogOpen={tariffDialogOpen}
        setTariffDialogOpen={setTariffDialogOpen}
        newTariff={newTariff}
        setNewTariff={setNewTariff}
        onCreateTariff={createTariff}
      />
    </Box>
  );
};

export default AssetsEditPage;
